import { Button, Drawer, Form, Input, Typography } from 'antd'
import { MinusOutlined, PlusOutlined } from '@ant-design/icons'
import React, { useEffect, useState } from 'react'
import dayjs from 'dayjs'
import dayjsGenerateConfig from 'rc-picker/lib/generate/dayjs'
import generatePicker from 'antd/es/date-picker/generatePicker'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import './ReleaseNotes.less'

const { TextArea } = Input
const { Text } = Typography
const DatePicker = generatePicker(dayjsGenerateConfig)

const MAX_CHARS = 100

const ReleaseNotesDrawer = ({
  initialValues,
  onClose,
  onPublish,
  onSave,
  open,
  mode,
}) => {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const [noteInput, setNoteInput] = useState('')
  const [notes, setNotes] = useState([])
  const [titleVersion, setTitleVersion] = useState('')
  const [releaseDate, setReleaseDate] = useState(null)

  const charCount = noteInput.length

  const isTitleFilled = !!titleVersion?.trim()
  const isSaveEnabled = isTitleFilled && !!releaseDate && notes.length > 0
  const isPublishEnabled = isTitleFilled && !!releaseDate && notes.length > 0

  useEffect(() => {
    if (open) {
      const prefill = initialValues ?? {}
      const prefillTitle = prefill.titleVersion ?? ''
      const prefillDate = prefill.releaseDate ?? null
      const prefillNotes = prefill.notes ?? []

      form.setFieldsValue({
        releaseDate: prefillDate,
        titleVersion: prefillTitle,
      })
      setNoteInput('')
      setNotes(prefillNotes)
      setTitleVersion(prefillTitle)
      setReleaseDate(prefillDate)
    }
  }, [open, initialValues, form])

  const handleValuesChange = changedValues => {
    if ('titleVersion' in changedValues) {
      setTitleVersion(changedValues.titleVersion ?? '')
    }
    if ('releaseDate' in changedValues) {
      setReleaseDate(changedValues.releaseDate ?? null)
    }
  }

  const handleNoteInputChange = e => {
    const value = e.target.value

    if (value.length <= MAX_CHARS) {
      setNoteInput(value)
    }
  }

  const handleAddNote = () => {
    const trimmed = noteInput.trim()

    if (!trimmed) return

    setNotes(prev => [...prev, trimmed])
    setNoteInput('')
  }

  const handleRemoveNote = index => {
    setNotes(prev => prev.filter((_, i) => i !== index))
  }

  const disabledDate = current => current && current < dayjs().startOf('day')

  const getValues = async () => {
    const values = await form.validateFields()

    return { ...values, notes }
  }

  const handleSave = async () => {
    try {
      const values = await getValues()

      onSave?.(values)
    } catch {
      // form validation failed
    }
  }

  const handlePublish = async () => {
    try {
      const values = await getValues()

      onPublish?.(values)
    } catch {
      // form validation failed
    }
  }

  const drawerFooter = (
    <div className="release-notes-drawer__footer">
      <Button onClick={onClose}>{t('releaseNotes.drawer.cancel')}</Button>
      <Button disabled={!isSaveEnabled} onClick={handleSave}>
        {t('releaseNotes.drawer.save')}
      </Button>
      <Button
        disabled={!isPublishEnabled}
        onClick={handlePublish}
        type="primary"
      >
        {t('releaseNotes.drawer.publish')}
      </Button>
    </div>
  )

  return (
    <Drawer
      destroyOnClose
      footer={drawerFooter}
      onClose={onClose}
      open={open}
      width={480}
      title={
        mode === 'edit'
          ? t('releaseNotes.drawer.titleEdit')
          : t('releaseNotes.drawer.title')
      }
    >
      <Form
        form={form}
        layout="vertical"
        onValuesChange={handleValuesChange}
        requiredMark={false}
      >
        {/* Title & Release version */}
        <Form.Item
          name="titleVersion"
          label={
            <span>
              {t('releaseNotes.drawer.fields.titleVersion')}
              <span className="release-notes-drawer__required-asterisk">
                {' '}
                *
              </span>
            </span>
          }
        >
          <Input
            placeholder={t('releaseNotes.drawer.placeholders.titleVersion')}
          />
        </Form.Item>

        {/* Release Date */}
        <Form.Item
          name="releaseDate"
          label={
            <span>
              {t('releaseNotes.drawer.fields.releaseDate')}
              <span className="release-notes-drawer__required-asterisk">
                {' '}
                *
              </span>
            </span>
          }
        >
          <DatePicker
            className="release-notes-drawer__date-picker"
            disabledDate={disabledDate}
            format="DD-MM-YYYY"
            placeholder="dd-mm-yyyy"
          />
        </Form.Item>

        {/* Notes */}
        <Form.Item
          label={
            <span>
              {t('releaseNotes.drawer.fields.notes')}
              <span className="release-notes-drawer__required-asterisk">
                {' '}
                *
              </span>
            </span>
          }
        >
          <div className="release-notes-drawer__notes-input-row">
            <TextArea
              autoSize={{ maxRows: 5, minRows: 3 }}
              onChange={handleNoteInputChange}
              onPressEnter={handleAddNote}
              placeholder={t('releaseNotes.drawer.placeholders.notes')}
              value={noteInput}
            />
            <Button
              aria-label={t('releaseNotes.drawer.fields.addNote')}
              className="release-notes-drawer__add-note-btn"
              icon={<PlusOutlined />}
              onClick={handleAddNote}
              type="primary"
            />
          </div>
          <Text className="release-notes-drawer__word-count" type="secondary">
            {charCount}/{MAX_CHARS} {t('releaseNotes.drawer.characters')}
          </Text>
        </Form.Item>

        {/* Added Notes list */}
        {notes.length > 0 && (
          <div className="release-notes-drawer__added-notes">
            <Text className="release-notes-drawer__added-notes-label" strong>
              {t('releaseNotes.drawer.addedNotes')}
            </Text>
            <div className="release-notes-drawer__notes-list">
              {notes.map((note, index) => (
                <div
                  className="release-notes-drawer__note-item"
                  key={`${note}-${index}`}
                >
                  <Text>{note}</Text>
                  <Button
                    aria-label={t('releaseNotes.drawer.fields.removeNote')}
                    className="release-notes-drawer__remove-note-btn"
                    icon={<MinusOutlined />}
                    onClick={() => handleRemoveNote(index)}
                    size="small"
                    type="text"
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </Form>
    </Drawer>
  )
}

ReleaseNotesDrawer.defaultProps = {
  initialValues: null,
  mode: 'create',
  onPublish: undefined,
  onSave: undefined,
}

ReleaseNotesDrawer.propTypes = {
  initialValues: PropTypes.shape({
    notes: PropTypes.arrayOf(PropTypes.string),
    releaseDate: PropTypes.object,
    titleVersion: PropTypes.string,
  }),
  mode: PropTypes.oneOf(['create', 'edit']),
  onClose: PropTypes.func.isRequired,
  onPublish: PropTypes.func,
  onSave: PropTypes.func,
  open: PropTypes.bool.isRequired,
}

export default ReleaseNotesDrawer
